import { useMemo } from 'react';
import { useAnalyticsHook } from '../hooks/useAnalyticsHook';
import type {
  AgeDistributionItem,
  MonthWiseAnalyticsResponse,
  PeriodSummary,
  StatCardConfig,
  TrendPoint,
} from '../types/totalAnalytics.types';
import {
  buildDateRangeLabel,
  buildPeriodSummary,
  buildStatCards,
  mapAgeDistribution,
  mapMonthWiseToTrends,
} from '../utils/totalAnalytics.utils';

export function useTotalAnalyticsPage() {
  const {
    handleStatsAnalytics,
    handleMonthWiseAnalytics,
    handleAgeDistributionAnalytics,
  } = useAnalyticsHook();

  const statsData = (handleStatsAnalytics.data?.data ??
    handleStatsAnalytics.data) as Record<string, unknown> | undefined;

  const monthWiseResponse = (handleMonthWiseAnalytics.data?.data ??
    handleMonthWiseAnalytics.data) as MonthWiseAnalyticsResponse | undefined;

  const ageDistributionResponse =
    handleAgeDistributionAnalytics.data?.data ??
    handleAgeDistributionAnalytics.data;

  const cards = useMemo<StatCardConfig[]>(
    () => buildStatCards(statsData, monthWiseResponse),
    [monthWiseResponse, statsData],
  );

  const trendsData = useMemo<TrendPoint[]>(
    () => mapMonthWiseToTrends(monthWiseResponse),
    [monthWiseResponse],
  );

  const ageDistribution = useMemo<AgeDistributionItem[]>(
    () => mapAgeDistribution(ageDistributionResponse),
    [ageDistributionResponse],
  );

  const ageTotal = useMemo(
    () => ageDistribution.reduce((sum, item) => sum + item.count, 0),
    [ageDistribution],
  );

  const dateRangeLabel = useMemo(
    () => buildDateRangeLabel(monthWiseResponse?.dateRange),
    [
      monthWiseResponse?.dateRange?.startDate,
      monthWiseResponse?.dateRange?.endDate,
    ],
  );

  const periodSummary = useMemo<PeriodSummary>(
    () => buildPeriodSummary(monthWiseResponse, trendsData, dateRangeLabel),
    [dateRangeLabel, monthWiseResponse, trendsData],
  );

  return {
    cards,
    trendsData,
    ageDistribution,
    ageTotal,
    dateRangeLabel,
    periodSummary,
    isLoading:
      handleStatsAnalytics.isPending ||
      handleMonthWiseAnalytics.isPending ||
      handleAgeDistributionAnalytics.isPending,
  };
}
